import { useState, useEffect } from "react";
import { Paper, Box, Typography, Divider, Stack, IconButton, TextField, MenuItem } from "@mui/material";
import { Edit } from "@mui/icons-material";
import ConfirmationModal from "../../../components/confirmation-modal";
import { StyledDatePicker, datePickerPopperSx } from "../../../components/ui/CalendarPopper";
import { DatePicker } from "@mui/x-date-pickers/DatePicker";
import { LocalizationProvider } from "@mui/x-date-pickers/LocalizationProvider";
import { AdapterDayjs } from "@mui/x-date-pickers/AdapterDayjs";
import dayjs from "dayjs";

const LICENSE_STATUS_OPTIONS = ["Active", "Expired", "Pending", "Suspended"];

// ---- inline: date formatter (was ../../../../../../utils/dateUtils) ----
const formatDate = (dateString) => {
	if (!dateString) return "";
	const date = dayjs(dateString);
	return date.isValid() ? date.format("MMM D, YYYY") : "";
};

// ---- inline: Row (was ClientDatabase/new/utils/rowCreator) ----
const Row = ({ label, value }) => (
	<Box sx={{ display: "flex", gap: 10, alignItems: "center" }} px={2}>
		<Typography variant="body2" color="text.secondary" sx={{ minWidth: "140px", flexShrink: 0 }}>
			{label}:
		</Typography>
		<Typography variant="body2">{value || "—"}</Typography>
	</Box>
);

const AgentPersonalDetails = ({ currentAgent, onSaveDetails, updateProfileLoading }) => {
	const [isEdit, setisEdit] = useState(false);
	const [confirmOpen, setConfirmOpen] = useState(false);

	const [detailsData, setDetailsData] = useState({
		first_name: currentAgent?.first_name || "",
		last_name: currentAgent?.last_name || "",
		email: currentAgent?.email || "",
		phone: currentAgent?.phone || "",
		dob: currentAgent?.dob || "",
		address: currentAgent?.address || "",
		agency_name: currentAgent?.agency_name || "",
		npn_number: currentAgent?.npn_number || "",
		license_number: currentAgent?.license_number || "",
		license_status: currentAgent?.license_status || "",
	});

	useEffect(() => {
		if (!updateProfileLoading) {
			setConfirmOpen(false);
			setisEdit(false);
		}
	}, [updateProfileLoading]);

	const handleUpdateDetails = (field) => (valueOrEvent) => {
		const value = valueOrEvent?.target?.value ?? valueOrEvent;

		setDetailsData((prev) => ({
			...prev,
			[field]: value,
		}));
	};

	const handleSaveClick = () => {
		setConfirmOpen(true);
	};

	const handleConfirmSave = async () => {
		//append both the first and lastname for the displayname
		const updatedDisplayName = `${detailsData.first_name || ""} ${detailsData.last_name || ""}`.trim();

		const payload = {
			...currentAgent,
			...detailsData,
			display_name: updatedDisplayName,
		};

		await onSaveDetails(payload);
		setisEdit(false);
	};

	const resetDetailsData = () => {
		setDetailsData({
			first_name: currentAgent?.first_name || "",
			last_name: currentAgent?.last_name || "",
			email: currentAgent?.email || "",
			phone: currentAgent?.phone || "",
			dob: currentAgent?.dob || "",
			address: currentAgent?.address || "",
			agency_name: currentAgent?.agency_name || "",
			npn_number: currentAgent?.npn_number || "",
			license_number: currentAgent?.license_number || "",
			license_status: currentAgent?.license_status || "",
		});
	};

	const inputStyle = {
		px: 2,
		"& .MuiInputBase-input": {
			padding: "6px 14px",
		},
		"& .MuiOutlinedInput-input": {
			border: "none !important",
		},
	};

	return (
		<LocalizationProvider dateAdapter={AdapterDayjs}>
			<Paper variant="outlined" sx={{ borderRadius: 2, mb: 2, width: "100% !important" }}>
				<Stack
					direction={"row"}
					justifyContent={"space-between !important"}
					alignItems="center !important"
					px={2}
					py={1.5}
					sx={{ backgroundColor: "secondary.lightbgcolor" }}
				>
					<Typography fontWeight={600} color="text.primary">
						Personal Details
					</Typography>
					{!isEdit ? (
						<IconButton
							onClick={() => setisEdit(true)}
							size="small"
							sx={{
								color: "secondary.main",
								backgroundColor: "transparent !important",
								"&:hover": {
									color: "primary.main",
								},
							}}
						>
							<Edit fontSize="small" />
						</IconButton>
					) : (
						<Stack direction={"row"} spacing={2}>
							<Box
								onClick={handleSaveClick}
								sx={{
									display: "inline-block",
									color: "primary.main",
									cursor: "pointer",
									textDecoration: "underline",
									"&:hover": { textDecoration: "underline" },
									fontWeight: 500,
								}}
							>
								<Typography variant="body2">Save</Typography>
							</Box>
							<Box
								onClick={() => {
									resetDetailsData();
									setisEdit(false);
								}}
								sx={{
									display: "inline-block",
									color: "primary.main",
									cursor: "pointer",
									textDecoration: "underline",
									"&:hover": { textDecoration: "underline" },
									fontWeight: 500,
								}}
							>
								<Typography variant="body2">Cancel</Typography>
							</Box>
						</Stack>
					)}
				</Stack>
				<Divider />
				{!isEdit ? (
					<Stack spacing={1.5} py={1.5}>
						<Row label="First name" value={detailsData.first_name || "Not Set"} />
						<Divider />
						<Row label="Last name" value={detailsData.last_name || "Not Set"} />
						<Divider />
						<Row label="Email" value={detailsData.email || "Not Set"} />
						<Divider />
						<Row label="Phone" value={detailsData.phone || "Not Set"} />
						<Divider />
						<Row label="Date of birth" value={formatDate(detailsData.dob) || "Not Set"} />
						<Divider />
						<Row label="Address" value={detailsData.address || "Not Set"} />
						<Divider />
						<Row label="Agency name" value={detailsData.agency_name || "Not Set"} />
						<Divider />
						<Row label="NPN number" value={detailsData.npn_number || "Not Set"} />
						<Divider />
						<Row label="License number" value={detailsData.license_number || "Not Set"} />
						<Divider />
						<Row label="License status" value={detailsData.license_status || "Not Set"} />
					</Stack>
				) : (
					<Stack spacing={1.5} py={1.5}>
						<TextField
							placeholder="First name..."
							value={detailsData.first_name}
							onChange={handleUpdateDetails("first_name")}
							fullWidth
							size="small"
							sx={inputStyle}
						/>

						<TextField
							placeholder="Last name..."
							value={detailsData.last_name}
							onChange={handleUpdateDetails("last_name")}
							fullWidth
							size="small"
							sx={inputStyle}
						/>

						<TextField
							placeholder="Email..."
							value={detailsData.email}
							onChange={handleUpdateDetails("email")}
							fullWidth
							size="small"
							sx={inputStyle}
						/>

						<TextField
							placeholder="Phone..."
							value={detailsData.phone}
							onChange={handleUpdateDetails("phone")}
							fullWidth
							size="small"
							sx={inputStyle}
						/>

						<StyledDatePicker
							value={detailsData.dob ? dayjs(detailsData.dob) : null}
							onChange={(newValue) => handleUpdateDetails("dob")(newValue && newValue.isValid() ? newValue.format("YYYY-MM-DD") : null)}
							slotProps={{
								textField: {
									fullWidth: true,
									size: "small",
									placeholder: "Date of birth...",
									sx: {
										px: 2,
										"& .MuiInputBase-input": {
											padding: "8.5px 14px",
										},
										"&:hover fieldset": {
											borderColor: "primary.main !important",
										},
										"&.Mui-focused fieldset": {
											borderColor: "primary.main !important",
										},
									},
								},
								popper: { sx: datePickerPopperSx },
							}}
						/>

						<TextField
							placeholder="Address..."
							value={detailsData.address}
							onChange={handleUpdateDetails("address")}
							fullWidth
							size="small"
							sx={inputStyle}
						/>

						<TextField
							placeholder="Agency name..."
							value={detailsData.agency_name}
							onChange={handleUpdateDetails("agency_name")}
							fullWidth
							size="small"
							sx={inputStyle}
						/>

						<TextField
							placeholder="NPN number..."
							value={detailsData.npn_number}
							onChange={handleUpdateDetails("npn_number")}
							fullWidth
							size="small"
							sx={inputStyle}
						/>

						<TextField
							placeholder="License number..."
							value={detailsData.license_number}
							onChange={handleUpdateDetails("license_number")}
							fullWidth
							size="small"
							sx={inputStyle}
						/>

						<TextField
							select
							placeholder="License status..."
							value={detailsData.license_status}
							onChange={handleUpdateDetails("license_status")}
							fullWidth
							size="small"
							sx={inputStyle}
						>
							{LICENSE_STATUS_OPTIONS.map((option) => (
								<MenuItem key={option} value={option}>
									{option}
								</MenuItem>
							))}
						</TextField>
					</Stack>
				)}

				<ConfirmationModal
					open={confirmOpen}
					onClose={() => setConfirmOpen(false)}
					onConfirm={handleConfirmSave}
					title="Review Personal Details"
					description="Please review the information below before saving."
					confirmText="Save Changes"
					confirmColor="#3A7DC9"
					isLoading={updateProfileLoading}
				/>
			</Paper>
		</LocalizationProvider>
	);
};
export default AgentPersonalDetails;
